/**
 * Recovery flow FSM.
 *
 * Owns the three branches of the post-crash IndexedDB-replay UI:
 *   - resume: continue recording on top of the persisted chunks
 *   - submit: upload everything that's saved + poll until summarized
 *   - discard: clear the IDB record and forget about it
 *
 * For provisional offline meetings, submit also performs the deferred
 * server-side meeting creation before the upload — that's the only way the
 * chunks can land against a real meeting row when the original session
 * crashed before going online.
 */
import { MEETING_STATUSES } from "@lexicanon/shared"
import type { I18n } from "@lingui/core"
import { msg } from "@lingui/core/macro"
import { modals } from "@mantine/modals"
import { useCallback, useRef } from "react"
import { RECORDER_AUDIO_BITS_PER_SECOND } from "../../../hooks/useAudioPipeline"
import type { CompanyApi } from "../../../hooks/useCompanyApi"
import { clearChunksFromIDB, loadChunksFromIDB, type SavedRecording } from "../../../hooks/useIndexedDB"
import { fetchChunkUploadState, fetchMeeting, startBrowserMeeting } from "../../../lib/api"
import type { IRemoteUploadHandle, TRecordingState } from "../interfaces"

interface IProps {
  meetingId: string
  recovered: SavedRecording | null
  company: CompanyApi | null
  streamRef: React.MutableRefObject<MediaStream | null>
  startTimeRef: React.MutableRefObject<number>
  pausedElapsedRef: React.MutableRefObject<number>
  mediaRecorderRef: React.MutableRefObject<MediaRecorder | null>
  chunksRef: React.MutableRefObject<Blob[]>
  lastUploadedChunkIndexRef: React.MutableRefObject<number>
  uploadBatchIndexRef: React.MutableRefObject<number>
  absoluteBaseRef: React.MutableRefObject<number>
  lastAudioAtRef: React.MutableRefObject<number>
  /** Shared provisional-pending gate. Cleared on submit once the server row exists. */
  provisionalPendingRef: React.MutableRefObject<boolean>
  getMimeType: () => string | null
  doChunkUpload: (isFinal: boolean) => Promise<boolean>
  startElapsedTimer: () => void
  startPeriodicSave: () => void
  startChunkUploadTimer: () => void
  setElapsed: React.Dispatch<React.SetStateAction<number>>
  setRecovered: (saved: SavedRecording | null) => void
  setProvisionalPending: (pending: boolean) => void
  setState: (state: TRecordingState) => void
  setRecordingActive: (active: boolean) => void
  setError: (err: string) => void
  onRecordingStopped?: () => void
  /**
   * Desktop hybrid far-end channel. On submit, its IDB sibling is replayed and
   * uploaded BEFORE the room final so processing sees a complete remote file.
   * `undefined` on web (no remote sibling ever persisted).
   */
  remoteUpload?: IRemoteUploadHandle
  i18n: I18n
}

interface IUseRecoveryFlowResult {
  handleResumeRecovery: () => void
  handleSubmitRecovery: () => Promise<void>
  handleDiscardRecovery: () => void
}

export function useRecoveryFlow(props: IProps): IUseRecoveryFlowResult {
  const {
    meetingId,
    recovered,
    company,
    streamRef,
    startTimeRef,
    pausedElapsedRef,
    mediaRecorderRef,
    chunksRef,
    lastUploadedChunkIndexRef,
    uploadBatchIndexRef,
    absoluteBaseRef,
    lastAudioAtRef,
    provisionalPendingRef,
    getMimeType,
    doChunkUpload,
    startElapsedTimer,
    startPeriodicSave,
    startChunkUploadTimer,
    setElapsed,
    setRecovered,
    setProvisionalPending,
    setState,
    setRecordingActive,
    setError,
    onRecordingStopped,
    remoteUpload,
    i18n,
  } = props

  // KNOWN LIMITATION (audit M1, deferred): resume rehydrates only the mic/room
  // channel. The pre-crash far-end ("remote") sibling is not reloaded here, and a
  // later clean finish clears both IDB keys — so resume-and-continue on a hybrid
  // recording drops the pre-crash far-end. The fix needs offset re-alignment +
  // careful seed/re-hunt ordering; tracked for a focused change. The common
  // recovery path (submit) preserves the far-end correctly.
  // Legacy (pre-v2) records don't know their upload-batch position — ask the
  // server. Every successful batch wrote exactly one receipt row, so the
  // receipt count IS the next batch index.
  // biome-ignore lint/correctness/useExhaustiveDependencies: refs are stable
  const reconcileLegacyBatchIndex = useCallback(async (): Promise<boolean> => {
    if (!company) return false
    try {
      const state = await fetchChunkUploadState(company, meetingId, "room")
      if (state.chunkCount === 0) return false
      uploadBatchIndexRef.current = state.chunkCount
      return true
    } catch {
      return false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs are stable
  }, [company, meetingId])

  // biome-ignore lint/correctness/useExhaustiveDependencies: streamRef + mediaRecorderRef are stable refs; setters are stable from useState; i18n is stable
  const handleResumeRecovery = useCallback((): void => {
    if (!recovered) return
    const stream = streamRef.current
    if (!stream) {
      setError(i18n._(msg`No microphone stream available`))
      return
    }
    const mimeType = getMimeType()
    if (!mimeType) return
    chunksRef.current = recovered.chunks
    lastUploadedChunkIndexRef.current = 0
    if (recovered.legacyTail && !recovered.isProvisional) {
      // Pre-v2 record: `chunks` is only the un-uploaded TAIL. Replaying it from
      // batch 0 would overwrite the server's good recording with headerless
      // data — resume the upload sequence at the server's chunk count instead.
      // The IDB gold copy can't be reconstructed for such a session (the head
      // was trimmed before v2 existed): park the persist cursor past any valid
      // seq so the append gap-guard rejects saves — the idbWarning banner then
      // tells the user local backup is degraded for this session.
      void reconcileLegacyBatchIndex()
      absoluteBaseRef.current = Number.MAX_SAFE_INTEGER
    } else {
      // v2 gold copy (or a provisional record, which was never uploaded): the
      // buffer is the COMPLETE recording — batch 0 legitimately restarts the
      // server file, and IDB persistence continues from seq 0.
      uploadBatchIndexRef.current = 0
      absoluteBaseRef.current = 0
    }
    startTimeRef.current = Date.now()
    pausedElapsedRef.current = Math.floor((recovered.savedAt - recovered.startTime) / 1000)
    setElapsed(pausedElapsedRef.current)
    setRecovered(null)
    const recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: RECORDER_AUDIO_BITS_PER_SECOND })
    recorder.ondataavailable = (e): void => {
      lastAudioAtRef.current = Date.now()
      if (e.data.size > 0) chunksRef.current.push(e.data)
    }
    recorder.start(1000)
    mediaRecorderRef.current = recorder
    setState("recording")
    setRecordingActive(true)
    startElapsedTimer()
    startChunkUploadTimer()
    startPeriodicSave()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see biome-ignore on declaration
  }, [
    recovered,
    setRecordingActive,
    startPeriodicSave,
    startElapsedTimer,
    startChunkUploadTimer,
    getMimeType,
    chunksRef,
    lastUploadedChunkIndexRef,
    pausedElapsedRef,
    setElapsed,
    startTimeRef,
    streamRef.current,
    uploadBatchIndexRef,
    reconcileLegacyBatchIndex,
    i18n._,
  ])

  // Re-entry guard — submit is async (create meeting → upload → poll). A second
  // click would otherwise fire a duplicate create/upload. See §1.5.
  const submitPendingRef = useRef(false)

  // biome-ignore lint/correctness/useExhaustiveDependencies: setters are stable from useState; i18n is stable
  const runSubmitRecovery = useCallback(async (): Promise<void> => {
    if (!recovered || !company) return

    if (recovered.isProvisional && recovered.subject) {
      try {
        await startBrowserMeeting(company, recovered.subject, recovered.templateId, recovered.meetingId)
        // The server row now exists. Clear the provisional gate, otherwise the
        // room/remote `doChunkUpload` calls below short-circuit on their first
        // line (`if (provisionalPendingRef.current) return false`) and the
        // freshly-created meeting is left empty. (Mirrors useOnlineFlush, which
        // already resets the flag after creation.)
        provisionalPendingRef.current = false
        setProvisionalPending(false)
      } catch {
        setError(i18n._(msg`Could not reach the server. Check your connection and try again.`))
        return
      }
    }

    // Far-end sibling (desktop hybrid): replay + upload its complete file FIRST
    // so the room final's processing sees {id}.remote.webm. Best-effort — a
    // missing/failed remote upload still lets the mic recording finalize. We
    // track its outcome so we only clear the remote IDB record if it actually
    // landed (see below).
    let remoteOk = true
    if (remoteUpload) {
      const remoteSaved = await loadChunksFromIDB(meetingId, "remote")
      if (remoteSaved && remoteSaved.chunks.length > 0) {
        remoteUpload.chunksRef.current = remoteSaved.chunks
        remoteUpload.lastUploadedChunkIndexRef.current = 0
        if (remoteSaved.legacyTail && !remoteSaved.isProvisional) {
          // Tail-only legacy record: resume the server sequence, never restart
          // it (a batch-0 replay would clobber the far-end file's head).
          try {
            const state = await fetchChunkUploadState(company, meetingId, "remote")
            if (state.chunkCount === 0) {
              // Server lost the head and the tail alone is unusable — keep the
              // IDB record for support-assisted recovery, skip the far-end.
              remoteOk = false
            } else {
              remoteUpload.uploadBatchIndexRef.current = state.chunkCount
            }
          } catch {
            remoteOk = false
          }
        } else {
          remoteUpload.uploadBatchIndexRef.current = 0
          remoteUpload.absoluteBaseRef.current = 0
        }
        if (remoteOk) {
          remoteUpload.offsetSecRef.current = remoteSaved.offsetSec
          remoteOk = await remoteUpload.doChunkUpload(true).catch(() => false)
        }
      }
    }

    chunksRef.current = recovered.chunks
    lastUploadedChunkIndexRef.current = 0
    if (recovered.legacyTail && !recovered.isProvisional) {
      // Pre-v2 record: only the un-uploaded tail survived locally. Resume the
      // server's upload sequence — restarting at batch 0 would atomically
      // replace the good recording with headerless data (the 2026-07-02
      // corruption). If the server has nothing to resume onto, the recording
      // can't be reconstructed from the tail alone: keep the local data and
      // tell the user instead of destroying evidence.
      const reconciled = await reconcileLegacyBatchIndex()
      if (!reconciled) {
        setError(
          i18n._(
            msg`The beginning of this recording is missing locally and the server has no copy to resume from. The saved audio was kept on this device — contact support to recover it.`,
          ),
        )
        return
      }
    } else {
      // Complete local copy (v2 gold copy, or provisional = never uploaded):
      // a full batch-0 replay rebuilds the server file from scratch.
      uploadBatchIndexRef.current = 0
      absoluteBaseRef.current = 0
    }
    setRecovered(null)
    setState("finalizing")
    const uploaded = await doChunkUpload(true)
    if (uploaded) {
      for (let i = 0; i < 120; i++) {
        await new Promise((r) => setTimeout(r, 2000))
        try {
          const d = (await fetchMeeting(company, meetingId)) as { status: string }
          if (d?.status === MEETING_STATUSES.SUMMARIZED) {
            // Gold copy: clear ONLY once the server confirms full processing.
            // Channel-scoped — a failed far-end stays in IDB for the next
            // recovery attempt.
            await clearChunksFromIDB(meetingId, "room")
            if (remoteOk) await clearChunksFromIDB(meetingId, "remote")
            break
          }
        } catch {}
      }
    } else {
      setError(i18n._(msg`Upload failed. Recovery data is still saved locally.`))
    }
    sessionStorage.removeItem(`provisional:${meetingId}`)
    setState("idle")
    onRecordingStopped?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n is stable
  }, [
    recovered,
    meetingId,
    onRecordingStopped,
    company,
    doChunkUpload,
    chunksRef,
    lastUploadedChunkIndexRef,
    uploadBatchIndexRef,
    absoluteBaseRef,
    provisionalPendingRef,
    setProvisionalPending,
    remoteUpload,
    i18n._,
  ])

  const handleSubmitRecovery = useCallback(async (): Promise<void> => {
    if (submitPendingRef.current) return
    submitPendingRef.current = true
    try {
      await runSubmitRecovery()
    } finally {
      submitPendingRef.current = false
    }
  }, [runSubmitRecovery])

  // Discard permanently destroys the recovered audio — there is no undo and no
  // server copy (the meeting may never have reached the backend). Confirm before
  // clearing IDB so a stray click can't lose a meeting. See §1.4.
  const handleDiscardRecovery = useCallback((): void => {
    modals.openConfirmModal({
      title: i18n._(msg`Discard recovered recording?`),
      children: i18n._(
        msg`Permanently delete the recovered recording? This can't be undone — the audio only exists on this device.`,
      ),
      labels: { confirm: i18n._(msg`Delete recording`), cancel: i18n._(msg`Keep it`) },
      confirmProps: { color: "danger" },
      onConfirm: () => {
        setRecovered(null)
        clearChunksFromIDB(meetingId)
      },
    })
  }, [meetingId, setRecovered, i18n])

  return { handleResumeRecovery, handleSubmitRecovery, handleDiscardRecovery }
}
